import { useI18n } from "vue-i18n";

import { CheckInOutcome, IRedeemResult } from "@/types/check-in";
import { ICheckInTicket } from "./useRedeemableLines";

export enum OutcomeSeverity {
  Success = "success",
  /** The ticket is not for now. This is not an error. */
  Info = "info",
  Error = "error",
}

export interface IFailureGroup {
  key: string;
  categoryName: string;
  startDateTime: string | null;
  count: number;
  message: string;
  severity: OutcomeSeverity;
}

const SEVERITIES: Record<CheckInOutcome, OutcomeSeverity> = {
  [CheckInOutcome.Ok]: OutcomeSeverity.Success,
  [CheckInOutcome.NotEligibleNow]: OutcomeSeverity.Info,
  [CheckInOutcome.NoAccessToArea]: OutcomeSeverity.Error,
  [CheckInOutcome.OutsideTimeslot]: OutcomeSeverity.Error,
  [CheckInOutcome.EntryLimitReached]: OutcomeSeverity.Error,
  [CheckInOutcome.IntervalNotElapsed]: OutcomeSeverity.Error,
  [CheckInOutcome.Expired]: OutcomeSeverity.Error,
  [CheckInOutcome.WrongVenue]: OutcomeSeverity.Error,
  [CheckInOutcome.AlreadyRedeemed]: OutcomeSeverity.Error,
};

export interface ICheckInOutcomesApi {
  messageFor: (result: IRedeemResult) => string;
  groupFailures: (
    results: IRedeemResult[],
    ticketsById: Map<number, ICheckInTicket>
  ) => IFailureGroup[];
}

/**
 * Turn engine outcomes into text and severity for the operator.
 *
 * @returns {ICheckInOutcomesApi} the message, severity and group functions.
 */
export function useCheckInOutcomes(): ICheckInOutcomesApi {
  const { t, te } = useI18n({ useScope: "global" });

  /**
   * Get the severity of an outcome. An unknown code counts as an error.
   *
   * @param {CheckInOutcome} outcome the outcome of the engine.
   * @returns {OutcomeSeverity} the severity.
   */
  function severityOf(outcome: CheckInOutcome): OutcomeSeverity {
    return SEVERITIES[outcome] ?? OutcomeSeverity.Error;
  }

  /**
   * Get the text for a result. The engine message is the fallback, so the
   * operator never sees an empty line.
   *
   * NOT_ELIGIBLE_NOW collects every code the engine does not map, so its
   * own message names the real cause (not paid, cancelled, not booked).
   * Show that message first, because our text for this code says nothing.
   *
   * @param {IRedeemResult} result one result of the engine.
   * @returns {string} the text to show.
   */
  function messageFor(result: IRedeemResult): string {
    if (result.outcome === CheckInOutcome.NotEligibleNow && result.message) {
      return result.message;
    }

    const key = `check-in.outcome.${result.outcome
      .toLowerCase()
      .replace(/_/g, "-")}`;

    return te(key) ? t(key) : result.message || "";
  }

  /**
   * Group the results that failed. Identical problems on the same category
   * and slot become one line with a count.
   *
   * @param {IRedeemResult[]} results all results of one action.
   * @param {Map<number, ICheckInTicket>} ticketsById the tickets that were sent.
   * @returns {IFailureGroup[]} one entry for each group of failures.
   */
  function groupFailures(
    results: IRedeemResult[],
    ticketsById: Map<number, ICheckInTicket>
  ): IFailureGroup[] {
    const groups = new Map<string, IFailureGroup>();

    for (const result of results) {
      if (result.outcome === CheckInOutcome.Ok) {
        continue;
      }

      const ticket = ticketsById.get(result.pssId);
      const key = [
        result.outcome,
        ticket?.categoryId ?? "none",
        ticket?.startDateTime ?? "none",
      ].join("|");

      const group = groups.get(key);
      if (group) {
        group.count += 1;
        continue;
      }

      groups.set(key, {
        key,
        categoryName: ticket?.categoryName || "",
        startDateTime: ticket?.startDateTime ?? null,
        count: 1,
        message: messageFor(result),
        severity: severityOf(result.outcome),
      });
    }

    return [...groups.values()];
  }

  return { messageFor, groupFailures };
}
